"use strict";
/**
 * State Change Handler
 * Manages application-wide state changes and polling lifecycle
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.handleStateChange = handleStateChange;
const ui_1 = require("../ui");
const polling_service_1 = require("./polling-service");
const data_source_service_1 = require("./data-source-service");
const events_handler_1 = require("../handlers/events-handler");
const gates_1 = require("../gates");
const sibling_markets_1 = require("../markets/sibling-markets");
/**
 * Handle state changes
 * Manages polling lifecycle based on page and market selection
 *
 * Key principle: Single source of truth for polling management
 * - Only handleStateChange manages start/stop of polling
 * - Other handlers only update state
 * - This prevents start/stop loops and race conditions
 */
// Track previous state to avoid unnecessary re-renders
let previousMarketId = null;
let previousPage = null;
let previousAuthState = false;
let previousDataSource = "kalshi";
// Inputs to permission gating — re-apply only when one of these changes so we
// never touch the DOM for gating during unrelated churn (e.g. isPlacingOrder).
let previousPermissions = null;
let previousDollarAmount = null;
function handleStateChange(state) {
    const isOnOrderPage = state.currentPage === "order";
    const hasSelectedMarket = !!state.selectedMarket;
    const isPlacingOrder = state.isPlacingOrder;
    const currentMarketId = state.selectedMarket?.id;
    const currentlyPollingMarketId = (0, polling_service_1.getCurrentlyPollingMarketId)();
    // Track if market or page changed
    const marketChanged = currentMarketId !== previousMarketId;
    const pageChanged = state.currentPage !== previousPage;
    const authChanged = state.isAuthenticated !== previousAuthState;
    const dataSourceChanged = state.dataSource !== previousDataSource;
    // --- AUTHENTICATION STATE CHANGE ---
    // If user just authenticated and is on events page with Polymarket selected,
    // we need to fetch sports and load markets
    if (authChanged &&
        state.isAuthenticated &&
        state.currentPage === "events" &&
        state.dataSource !== "kalshi") {
        console.log(`[handleStateChange] User authenticated with ${state.dataSource}, fetching sports and markets`);
        const categorySelector = document.getElementById("market-category");
        if (categorySelector) {
            (0, data_source_service_1.populateFlatLeagueSelector)(categorySelector, state.dataSource)
                .then(() => {
                const firstSport = categorySelector?.value;
                if (firstSport) {
                    console.log("[handleStateChange] Loading markets for sport:", firstSport);
                    (0, events_handler_1.loadEvents)(firstSport);
                }
            })
                .catch((error) => {
                console.error(`[handleStateChange] Failed to populate ${state.dataSource} leagues:`, error);
            });
        }
    }
    // --- STOP POLLING LOGIC ---
    // Stop polling if:
    // - Not on order page (navigated away)
    // - No selected market
    // - Market ID changed (will restart below for new market)
    const shouldStopPolling = !isOnOrderPage ||
        !hasSelectedMarket ||
        (currentlyPollingMarketId !== null &&
            currentlyPollingMarketId !== currentMarketId);
    if (shouldStopPolling && currentlyPollingMarketId !== null) {
        console.log(`🛑 Stopping polling: isOnOrderPage=${isOnOrderPage}, hasSelectedMarket=${hasSelectedMarket}`);
        (0, polling_service_1.stopAllPolling)();
    }
    // --- START/RESTART POLLING LOGIC ---
    // Only start if we are on the order page,
    // AND we have a selected market, AND we're not already polling it
    if (isOnOrderPage &&
        hasSelectedMarket &&
        currentMarketId &&
        currentlyPollingMarketId !== currentMarketId) {
        console.log(`🟢 Starting polling for market: ${currentMarketId}`);
        (0, polling_service_1.startPollingForMarket)(currentMarketId, state.selectedMarket.clobTokenIds);
    }
    // --- UI RENDERING ---
    // Only update page if it changed
    if (pageChanged) {
        (0, ui_1.showPage)(state.currentPage);
    }
    // Only update market display if market changed or we just navigated to order page
    if (state.selectedMarket && state.currentPage === "order") {
        if (marketChanged || (pageChanged && isOnOrderPage)) {
            (0, ui_1.displaySelectedMarket)(state.selectedMarket);
            (0, ui_1.updateSlippageSlider)(state.tradingSettings.slippageTolerancePercent);
            // Sync slippage UI using the centralized visibility rules so
            // manual/baseball mode stays authoritative after reload/state changes.
            const isIndependent = state.tradingSettings.independentSlippage === true;
            (0, ui_1.updateSlippageVisibility)(state.tradingSettings);
            if (isIndependent) {
                (0, ui_1.updateIndependentSlippageSlider)("a", state.tradingSettings.slippageTolerancePercentA ??
                    state.tradingSettings.slippageTolerancePercent);
                (0, ui_1.updateIndependentSlippageSlider)("b", state.tradingSettings.slippageTolerancePercentB ??
                    state.tradingSettings.slippageTolerancePercent);
            }
        }
    }
    // Update market selection summary on order page
    (0, ui_1.updateMarketSelectionSummary)(isOnOrderPage ? state.selectedMarket : null, isOnOrderPage ? state.parallelMarkets : [], isOnOrderPage
        ? (0, sibling_markets_1.siblingMarkets)(state.selectedMarket, state.availableMarkets ?? [], state.parallelMarkets ?? [])
        : []);
    (0, ui_1.updateSettingsDisplay)(state.tradingSettings);
    // Apply permission-driven UI gating (hide/disable controls, badge, cap
    // hint). Cosmetic only — the server enforces every cap/feature. Re-applied
    // only when its inputs change (permissions, page, or the dollar amount that
    // drives the bet-cap backstop) so unrelated state churn never touches the
    // DOM for gating. See gates.ts.
    const permissionsChanged = state.permissions !== previousPermissions;
    const dollarAmountChanged = state.tradingSettings.dollarAmount !== previousDollarAmount;
    if (permissionsChanged || pageChanged || dollarAmountChanged) {
        (0, gates_1.applyPermissionUi)();
    }
    // Update tracking variables
    previousMarketId = currentMarketId ?? null;
    previousPage = state.currentPage;
    previousAuthState = state.isAuthenticated;
    previousDataSource = state.dataSource;
    previousPermissions = state.permissions;
    previousDollarAmount = state.tradingSettings.dollarAmount;
}
